import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthContext";
import type { CreditBalance } from "@/lib/credit-types";
import { calculateCredits, type CreditEstimateInput, type CreditEstimateBreakdown } from "@/lib/credit-calculator";

// Stripe product/price mapping
export const SUBSCRIPTION_TIERS = {
  free: {
    name: "Free",
    product_ids: [] as string[],
    price_id_monthly: null,
    price_id_annual: null,
    monthlyPrice: 0,
    annualPrice: 0,
    monthlyCredits: 10,
    features: ["10 credits per month", "Quick clarity analysis", "Markdown export"],
  },
  solo: {
    name: "Solo",
    product_ids: ["prod_U3DtUXGvHQIHWY", "prod_U3DutOWpAW6Pmv"],
    price_id_monthly: "price_1T57RvPJ0ZCzH9fvuiLWB57V",
    price_id_annual: "price_1T57SgPJ0ZCzH9fvmJnjoxgx",
    monthlyPrice: 29,
    annualPrice: 290,
    monthlyCredits: 100,
    features: ["100 credits per month", "Quick & deep analysis", "CSV + Markdown export", "Credit top-ups"],
  },
  startup: {
    name: "Startup",
    product_ids: ["prod_U3Du4m8vxprRLf", "prod_U3DvIUKnMRfEBF"],
    price_id_monthly: "price_1T57T4PJ0ZCzH9fvy6Tr7zLX",
    price_id_annual: "price_1T57TPPJ0ZCzH9fvQ9d6UsLV",
    monthlyPrice: 99,
    annualPrice: 990,
    monthlyCredits: 500,
    features: ["500 credits per month", "5 team members", "Jira integration", "Credit top-ups"],
  },
  business: {
    name: "Business",
    product_ids: ["prod_U3Dvv1KRQ3cfsV", "prod_U3DvK03M4Rh3CS"],
    price_id_monthly: "price_1T57TgPJ0ZCzH9fvOtzx8qV3",
    price_id_annual: "price_1T57U7PJ0ZCzH9fvmpmS5BNK",
    monthlyPrice: 299,
    annualPrice: 2990,
    monthlyCredits: 2000,
    features: ["2,000 credits per month", "25 team members", "BYOK + SSO", "Credit top-ups"],
  },
} as const;

export type SubscriptionTier = keyof typeof SUBSCRIPTION_TIERS;

interface CreditUsageState {
  balance: CreditBalance | null;
  loading: boolean;
}

interface SubscriptionState {
  subscribed: boolean;
  tier: SubscriptionTier;
  productId: string | null;
  subscriptionEnd: string | null;
  isTrial: boolean;
  trialEndsAt: string | null;
  trialEligible: boolean;
  loading: boolean;
  error: string | null;
  credits: CreditUsageState;
}

interface SubscriptionContextType extends SubscriptionState {
  refreshSubscription: () => Promise<void>;
  refreshCredits: () => Promise<void>;
  createCheckout: (tier: SubscriptionTier, billingPeriod?: "monthly" | "annual", trial?: boolean) => Promise<string | null>;
  openCustomerPortal: () => Promise<void>;
  // Credit-based methods
  canAfford: (estimate: CreditEstimateBreakdown) => boolean;
  estimateCredits: (input: CreditEstimateInput) => CreditEstimateBreakdown;
  consumeCredits: (input: CreditEstimateInput & { runId?: string }) => Promise<{ success: boolean; message?: string }>;
  purchaseTopup: (priceId: string) => Promise<string | null>;
  // Legacy methods for backward compatibility
  canUseReview: () => boolean;
  incrementUsage: () => Promise<{ success: boolean; message?: string }>;
  usage: {
    creditsUsed: number;
    creditsLimit: number;
    loading: boolean;
  };
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

const getDefaultCreditBalance = (): CreditBalance => ({
  monthlyCredits: 10,
  monthlyUsed: 0,
  monthlyRemaining: 10,
  topupCredits: 0,
  totalRemaining: 10,
  monthlyResetAt: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toISOString(),
});

const getDefaultState = (): SubscriptionState => ({
  subscribed: false,
  tier: "free",
  productId: null,
  subscriptionEnd: null,
  isTrial: false,
  trialEndsAt: null,
  trialEligible: true,
  loading: false,
  error: null,
  credits: {
    balance: getDefaultCreditBalance(),
    loading: false,
  },
});

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user, session } = useAuth();
  const [state, setState] = useState<SubscriptionState>(getDefaultState);

  const refreshCredits = useCallback(async () => {
    if (!session) return;

    setState(prev => ({
      ...prev,
      credits: { ...prev.credits, loading: true },
    }));

    try {
      const { data, error } = await supabase.functions.invoke("check-usage");
      
      if (error) throw error;
      
      if (data.credits) {
        setState(prev => ({
          ...prev,
          credits: {
            balance: {
              monthlyCredits: data.credits.monthly_credits,
              monthlyUsed: data.credits.monthly_used,
              monthlyRemaining: data.credits.monthly_remaining,
              topupCredits: data.credits.topup_credits,
              totalRemaining: data.credits.total_remaining,
              monthlyResetAt: data.credits.monthly_reset_at,
            },
            loading: false,
          },
        }));
      }
    } catch (error) {
      console.error("Error checking credits:", error);
      setState(prev => ({
        ...prev,
        credits: { ...prev.credits, loading: false },
      }));
    }
  }, [session]);

  const refreshSubscription = useCallback(async () => {
    if (!session) {
      setState(getDefaultState());
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const { data, error } = await supabase.functions.invoke("check-subscription");
      
      if (error) throw error;
      
      const tier = (data.tier as SubscriptionTier) || "free";
      
      setState(prev => ({
        ...prev,
        subscribed: data.subscribed,
        tier,
        productId: data.product_id,
        subscriptionEnd: data.subscription_end,
        isTrial: data.is_trial ?? false,
        trialEndsAt: data.trial_ends_at ?? null,
        trialEligible: data.trial_eligible ?? true,
        loading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Error checking subscription:", error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : "Failed to check subscription",
      }));
    }
  }, [session]);

  const createCheckout = useCallback(async (tier: SubscriptionTier, billingPeriod: "monthly" | "annual" = "monthly", trial: boolean = false): Promise<string | null> => {
    if (!session) return null;
    
    const tierData = SUBSCRIPTION_TIERS[tier];
    const priceId = billingPeriod === "annual" ? tierData.price_id_annual : tierData.price_id_monthly;
    if (!priceId) {
      console.error("No price ID for tier:", tier, billingPeriod);
      return null;
    }

    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId, trial },
      });
      
      if (error) throw error;
      return data.url;
    } catch (error) {
      console.error("Error creating checkout:", error);
      return null;
    }
  }, [session]);

  const openCustomerPortal = useCallback(async () => {
    if (!session) return;

    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      
      if (error) throw error;
      
      if (data.url) {
        window.open(data.url, "_blank");
      }
    } catch (error) {
      console.error("Error opening customer portal:", error);
    }
  }, [session]);

  // Credit-based methods
  const estimateCredits = useCallback((input: CreditEstimateInput): CreditEstimateBreakdown => {
    return calculateCredits(input);
  }, []);

  const canAfford = useCallback((estimate: CreditEstimateBreakdown): boolean => {
    const balance = state.credits.balance;
    if (!balance) return false;
    return balance.totalRemaining >= estimate.totalCredits;
  }, [state.credits.balance]);

  const consumeCredits = useCallback(async (
    input: CreditEstimateInput & { runId?: string }
  ): Promise<{ success: boolean; message?: string }> => {
    if (!session) return { success: false, message: "Not authenticated" };

    try {
      const { data, error } = await supabase.functions.invoke("increment-usage", {
        body: {
          mode: input.mode,
          depth: input.depth,
          inputLength: input.inputLength,
          personaCount: input.personaCount,
          runId: input.runId,
        },
      });
      
      if (error) throw error;
      
      if (data.success) {
        // Update local state with new balance
        setState(prev => ({
          ...prev,
          credits: {
            ...prev.credits,
            balance: prev.credits.balance ? {
              ...prev.credits.balance,
              monthlyRemaining: data.monthly_remaining,
              topupCredits: data.topup_remaining,
              totalRemaining: data.credits_remaining,
              monthlyUsed: prev.credits.balance.monthlyCredits - data.monthly_remaining,
            } : null,
          },
        }));
        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error("Error consuming credits:", error);
      return { 
        success: false, 
        message: error instanceof Error ? error.message : "Failed to consume credits" 
      };
    }
  }, [session]);

  const purchaseTopup = useCallback(async (priceId: string): Promise<string | null> => {
    if (!session) return null;

    try {
      const { data, error } = await supabase.functions.invoke("purchase-topup", {
        body: { priceId },
      });
      
      if (error) throw error;
      return data.url;
    } catch (error) {
      console.error("Error creating topup checkout:", error);
      return null;
    }
  }, [session]);

  // Legacy methods for backward compatibility
  const canUseReview = useCallback(() => {
    const balance = state.credits.balance;
    if (!balance) return false;
    return balance.totalRemaining >= 1;
  }, [state.credits.balance]);

  const incrementUsage = useCallback(async (): Promise<{ success: boolean; message?: string }> => {
    // Use default values for legacy calls
    return consumeCredits({
      mode: "evaluation",
      depth: "quick",
      inputLength: 500,
      personaCount: 1,
    });
  }, [consumeCredits]);

  // Usage object mapping credits to plan
  const usage = {
    creditsUsed: state.credits.balance?.monthlyUsed ?? 0,
    creditsLimit: state.credits.balance?.monthlyCredits ?? SUBSCRIPTION_TIERS[state.tier].monthlyCredits,
    loading: state.credits.loading,
  };

  // Refresh subscription on auth change
  useEffect(() => {
    if (user) {
      refreshSubscription();
    } else {
      setState(getDefaultState());
    }
  }, [user, refreshSubscription]);

  // Refresh credits after subscription is loaded
  useEffect(() => {
    if (user && !state.loading && state.tier) {
      refreshCredits();
    }
  }, [user, state.loading, state.tier, refreshCredits]);

  // Auto-refresh every minute
  useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(() => {
      refreshSubscription();
      refreshCredits();
    }, 60000);
    return () => clearInterval(interval);
  }, [user, refreshSubscription, refreshCredits]);

  return (
    <SubscriptionContext.Provider
      value={{
        ...state,
        refreshSubscription,
        refreshCredits,
        createCheckout,
        openCustomerPortal,
        canAfford,
        estimateCredits,
        consumeCredits,
        purchaseTopup,
        // Legacy
        canUseReview,
        incrementUsage,
        usage,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
}
